/**
 * `commitments` procedure group — design §1.10 / §3.4.
 *
 * "Promised intros as FIRST-CLASS OBJECTS with verbatim quote, source
 * interaction, confidence and status. This is the mechanism that makes Hermes
 * feature 8 real rather than prose buried in a summary."
 *
 * The `quote` field is not decoration and is not optional in spirit: it is what
 * lets a drafted follow-up say "*you mentioned you'd introduce me to Priya at
 * Sequoia*" and be provably grounded. The `quoteOffset` next to it is what lets
 * the UI highlight the exact span in the source note rather than re-searching
 * for the phrase and finding the wrong occurrence.
 */

import { z } from 'zod';
import { isoDateTimeSchema, uuidSchema } from './common.ts';
import { commitmentKindSchema, commitmentStatusSchema, owedBySchema } from './enums.ts';

export const commitmentSchema = z.object({
  id: uuidSchema,
  kind: commitmentKindSchema,
  /** `me` = I owe it; `them` = they owe me. */
  owedBy: owedBySchema,
  counterpartyContactId: uuidSchema.nullable(),
  /** The person to be introduced, when we resolved them. */
  subjectContactId: uuidSchema.nullable(),
  /** "someone at Sequoia" — the raw target when resolution failed. */
  subjectText: z.string().nullable(),
  summary: z.string(),
  /** Verbatim evidence, proven a real substring of the source before storage. */
  quote: z.string().nullable(),
  quoteOffset: z.int().min(0).nullable(),
  sourceType: z.string().nullable(),
  sourceInteractionId: uuidSchema.nullable(),
  confidence: z.number().min(0).max(1).nullable(),
  status: commitmentStatusSchema,
  dueAt: isoDateTimeSchema.nullable(),
  fulfilledAt: isoDateTimeSchema.nullable(),
  confirmedByUser: z.boolean(),
  createdAt: isoDateTimeSchema,
});

export const commitmentsListInputSchema = z.object({
  owedBy: owedBySchema.optional(),
  status: commitmentStatusSchema.optional(),
  kind: commitmentKindSchema.optional(),
  contactId: uuidSchema.optional(),
  /** The "they offered X ten days ago" nudge query. */
  olderThanDays: z.int().min(0).max(3650).optional(),
  limit: z.int().min(1).max(200).default(50),
});

export const commitmentsListOutputSchema = z.object({
  items: z.array(commitmentSchema),
});

export const commitmentUpdateInputSchema = z.object({
  commitmentId: uuidSchema,
  status: commitmentStatusSchema,
});

export const commitmentConfirmInputSchema = z.object({
  commitmentId: uuidSchema,
});

export type CommitmentDto = z.output<typeof commitmentSchema>;
